// eslint-disable-next-line import/no-cycle -- please fix if you can
import {flattenJSON} from './flattenJSON';

export type RecordStringUnknown = Record<string, unknown>;
export type JSONNode = RecordStringUnknown | unknown[];
export type JSONValue = string | number | boolean | null;

export interface FilterJSONOptions {
  nodeTransformFunction?: (n: JSONNode) => JSONNode | JSONValue;
  stringSearchFunction?: (testString: string) => boolean;
  excludeList?: string[];
}

/*
 * This function takes a JSON object and recurses through it, returning a new JSON object with
 * the same structure, but with some nodes removed or transformed.
 *
 * The options object can contain these properties:
 * - nodeTransformFunction: a function that takes a JSON node and returns a new JSON node
 * - stringFilterFunction: a function that takes a string and returns a boolean
 * - excludeList: an array of strings, where each string is a key that should be excluded from the output
 *
 * If nodeTransformFunction is provided, it will be called on each node in the JSON object.
 * If stringFilterFunction is provided, it will be called on each string in the JSON object. (currently only keys, but values will be added)
 *
 * If a node is an array, the function will map over its elements and apply the transform function.
 * If a node is an object, the function will transform its properties.
 * If a node is not an object or is null, the function will return it as is.
 */
export const filterJSON = ({
  node,
  options = {},
}: {
  node: JSONNode;
  options?: FilterJSONOptions;
}): unknown => {
  // const t0 = performance.now();

  const {
    nodeTransformFunction,
    stringSearchFunction,
    excludeList = [],
  } = options;

  const isTransforming = nodeTransformFunction != null;
  const isSearching = stringSearchFunction != null;

  const transformedNode = isTransforming ? nodeTransformFunction(node) : node;

  // If node is an array, map over its elements and apply the transform function
  if (Array.isArray(transformedNode)) {
    return transformedNode.map(n =>
      isJSONNode(n) ? filterJSON({node: n, options}) : n
    );
  }

  // If node is not an object or is null, return it as is
  if (typeof transformedNode !== 'object' || transformedNode === null) {
    return transformedNode;
  }

  // If node is an ordinary object, transform its properties
  const filteredNode: RecordStringUnknown = {};
  Object.entries(transformedNode).forEach(function filterNodeRecurse([
    key,
    value,
  ]) {
    // If the key is in the exclude list, skip it
    if (excludeList.includes(key)) {
      return;
    }

    // // If the value is not an object, return it as is
    // if (!isJSONNode(value)) {
    //   filteredNode[key] = value;
    //   return;
    // }

    // if (isArray(value)) {
    //   filteredNode[key] = value.map(n =>
    //     isJSONNode(n) ? filterJSON({node: n, options}) : n
    //   );
    //   return;
    // }

    // If we're not searching, continue traversing the descendents of this node
    if (!isSearching) {
      filteredNode[key] = isJSONNode(value)
        ? Array.isArray(value)
          ? // If the value is an array, map over its elements and apply the transform function
            value.map(n => (isJSONNode(n) ? filterJSON({node: n, options}) : n))
          : // If the value is an object, recurse into it
            filterJSON({node: value, options})
        : // If the value is not a JSONNode (array or object), return it as is
          value;
      return;
    }

    // TODO: value search only works on strings right now. get it working with other types.
    /* Search object values */
    // const flatValues = Object.values(flattenJson(value));
    // const isValueSearchMatch = isString(value) && filterFunction(value);
    // const isDescendentValueSearchMatch = flatValues.some(
    //   v => isString(v) && filterFunction(v)
    // );
    // if (isDescendentValueSearchMatch) {
    //   transformedNode[key] = transformJson({node: value, ...options});
    //   return;
    // }
    // if (isValueSearchMatch) {
    //   transformedNode[key] = value; // transformJson({node: value, ...options});
    //   return;
    // }

    const isKeySearchMatch = stringSearchFunction(key);

    // If the value is not an object, return it as is (if it matches the search)
    if (!isJSONNode(value)) {
      if (isKeySearchMatch) {
        filteredNode[key] = value;
      }
      return;
    }

    /* Search object keys */
    const flatKeys = Object.keys(flattenJSON({node: value}));
    const isDescendentKeySearchMatch = flatKeys.some(k =>
      stringSearchFunction(k)
    );
    // TODO: this is probably more performant than the line above, but doesn't match correctly on ^ and & in regex. if perf is an issue, try to fix this.
    // const isDescendentKeySearchMatch = filterFunction(flatKeys.join('\n'));

    if (isDescendentKeySearchMatch) {
      filteredNode[key] = filterJSON({node: value, options});
      return;
    }

    if (isKeySearchMatch) {
      filteredNode[key] = isJSONNode(value)
        ? filterJSON({
            node: value,
            // If the key itself is a search match, we want to show the whole object (all descendent keys and values),
            // so we remove the stringSearchFunction from the recursion here.
            options: {...options, stringSearchFunction: undefined},
          })
        : value;
      return;
    }

    return;
  });

  // const t1 = performance.now();
  // const duration = t1 - t0;
  // const LOG_THRESHOLD = 1;
  // if (duration > LOG_THRESHOLD) {
  //   console.log('filterJSON took', t1 - t0, 'milliseconds.');
  // }
  return filteredNode;
};

export const isJSONNode = (value: unknown): value is JSONNode => {
  if (Array.isArray(value)) {
    return true;
  }
  return typeof value === 'object' && value !== null;
};
